import React, { useState, useEffect } from "react";
import { X } from "lucide-react";

export const CouponForm = ({
  isOpen,
  onClose,
  onSubmit,
  initialData = null,
  categories = [],
}) => {
  const [formData, setFormData] = useState({
    code: "",
    discount: "",
    discountType: "percentage",
    expires: "",
    minimumItems: "",
    minimumAmount: "",
    ApplicableTo: [],
    isActive: true,
  });
  const [errors, setErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    if (initialData) {
      const expiryDate = initialData.expires
        ? new Date(initialData.expires).toISOString().split("T")[0]
        : "";

      setFormData({
        code: initialData.code || "",
        discount: initialData.discount || "",
        expires: expiryDate,
        discountType: initialData.discountType || "percentage",
        minimumItems: initialData.minimumItems || "",
        minimumAmount: initialData.minimumAmount || "",
        ApplicableTo: initialData.ApplicableTo || [],
        isActive: initialData.isActive ?? true,
      });
    } else {
      setFormData({
        code: "",
        discount: 0,
        expires: "",
        minimumItems: 0,
        minimumAmount: 0,
        ApplicableTo: [],
        isActive: true,
      });
    }
  }, [initialData, isOpen]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.code.trim()) newErrors.code = "Code is required";
    if (!formData.discount) newErrors.discount = "Discount is required";
    if (!formData.expires) newErrors.expires = "Expiration date is required";
    return newErrors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when field is edited
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const toggleCategory = (categoryId) => {
    setFormData((prev) => ({
      ...prev,
      ApplicableTo: prev.ApplicableTo.includes(categoryId)
        ? prev.ApplicableTo.filter((id) => id !== categoryId)
        : [...prev.ApplicableTo, categoryId],
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const date = new Date(formData.expires);
    date.setHours(23, 59, 59, 999);

    const submissionData = {
      ...formData,
      discount: Number(formData.discount),
      discountType: formData.discountType,
      minimumItems: formData.minimumItems
        ? Number(formData.minimumItems)
        : undefined,
      minimumAmount: formData.minimumAmount
        ? Number(formData.minimumAmount)
        : undefined,
      expires: date.toISOString(),
    };

    onSubmit(submissionData);
  };

  const filteredCategories =
    categories?.filter((category) =>
      category?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

  const selectedCategories =
    categories?.filter((cat) => formData.ApplicableTo.includes(cat?._id)) || [];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold">
              {initialData ? "Edit Coupon" : "Add New Coupon"}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Coupon Code*
              </label>
              <input
                type="text"
                name="code"
                value={formData.code}
                onChange={handleInputChange}
                className={`w-full p-2 border rounded-lg ${
                  errors.code ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.code && (
                <p className="text-red-500 text-sm mt-1">{errors.code}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Discount Type*
              </label>
              <select
                name="discountType"
                value={formData.discountType}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded-lg"
              >
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed Amount ($)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Discount{" "}
                {formData.discountType === "percentage" ? "(%)" : "($)"}*
              </label>
              <input
                type="number"
                name="discount"
                value={formData.discount}
                onChange={handleInputChange}
                min="0"
                max={formData.discountType === "percentage" ? "100" : undefined}
                className={`w-full p-2 border rounded-lg ${
                  errors.discount ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.discount && (
                <p className="text-red-500 text-sm mt-1">{errors.discount}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expiration Date*
              </label>
              <input
                type="date"
                name="expires"
                value={formData.expires}
                onChange={handleInputChange}
                min={new Date().toISOString().split("T")[0]}
                className={`w-full p-2 border rounded-lg ${
                  errors.expires ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.expires && (
                <p className="text-red-500 text-sm mt-1">{errors.expires}</p>
              )}
            </div>

            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Applicable Categories
              </label>
              <div className="relative">
                <div
                  className="w-full p-2 border border-gray-300 rounded-lg min-h-[42px] cursor-pointer flex flex-wrap gap-1"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  {selectedCategories.length > 0 ? (
                    selectedCategories.map((category) => (
                      <span
                        key={category._id}
                        className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm mr-1 mb-1"
                      >
                        {category.name}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCategory(category._id);
                          }}
                          className="ml-1 text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-500">Select categories...</span>
                  )}
                </div>

                {isDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10">
                    <div className="p-2 border-b">
                      <input
                        type="text"
                        placeholder="Search categories..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full p-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {filteredCategories.map((category) => (
                        <div
                          key={category._id}
                          className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                          onClick={() => toggleCategory(category._id)}
                        >
                          <input
                            type="checkbox"
                            checked={formData.ApplicableTo.includes(
                              category._id
                            )}
                            onChange={() => {}}
                            className="mr-2"
                          />
                          <span>{category.name}</span>
                        </div>
                      ))}
                      {filteredCategories.length === 0 && (
                        <div className="p-2 text-gray-500 text-center">
                          No categories found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Minimum Items
              </label>
              <input
                type="number"
                name="minimumItems"
                value={formData.minimumItems}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Minimum Amount
              </label>
              <input
                type="number"
                name="minimumAmount"
                value={formData.minimumAmount}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Active
              </label>
              <select
                name="isActive"
                value={formData.isActive}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={true}>Yes</option>
                <option value={false}>No</option>
              </select>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-white bg-blue-500 rounded-lg hover:bg-blue-600"
              >
                {initialData ? "Update" : "Add"} Coupon
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CouponForm;
